import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { publicOrigin } from "@/lib/landerDomain";
import { ChartLineUp, EyeSlash } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTrackingCode } from "@/lib/useTrackingCode";
import { ResultsFilters } from "@/components/results/ResultsFilters";
import { ResultsTotals } from "@/components/results/ResultsTotals";
import { ResultsBreakdown } from "@/components/results/ResultsBreakdown";
import { BestHours } from "@/components/results/BestHours";
import { ResultsTable } from "@/components/results/ResultsTable";
import { MediaBuyerLinksCard } from "@/components/results/MediaBuyerLinksCard";
import { WeeklyReportToggle } from "@/components/results/WeeklyReportToggle";
import { BusyAlertsCard } from "@/components/results/BusyAlertsCard";
import { WeeklyReportAdminCard } from "@/components/results/WeeklyReportAdminCard";

const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toLocaleDateString("en-CA"); };
const START = { date_from: daysAgo(29), date_to: daysAgo(0), q: "", state: "", page: "", source: "", outcome: "", media_buyer_id: "", include_test: false };

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => { clearTimeout(t); }; }, [value, ms]);
  return v;
}

export default function TrafficResults() {
  const { user } = useAuth();
  const isMb = user?.role === "media_buyer";
  const code = useTrackingCode(isMb);
  const [f, setF] = useState(START);
  const [pageNum, setPageNum] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const set = (patch) => { setF((p) => ({ ...p, ...patch })); setPageNum(1); };
  const q = useDebounced(f.q, 300);
  const source = useDebounced(f.source, 300);
  const params = useMemo(() => {
    const p = { ...f, q, source, page_num: pageNum, page_size: 50 };
    return Object.fromEntries(Object.entries(p).filter(([, v]) => v !== "" && v !== false));
  }, [f, q, source, pageNum]);

  useEffect(() => {
    setLoading(true);
    api.get("/traffic-results", { params })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load results"))
      .finally(() => setLoading(false));
  }, [params]);

  const showAdmin = !isMb;
  return (
    <div className="p-2" data-testid="traffic-results">
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <ChartLineUp size={22} weight="bold" />
        <h1 className="font-chivo font-black text-3xl tracking-tighter uppercase">Traffic results</h1>
        {isMb && <span className="label-mono inline-flex items-center gap-1"><EyeSlash size={11} weight="bold" /> Your leads · no contact details</span>}
        {loading && <span className="label-mono text-zinc-500" data-testid="results-loading">Loading…</span>}
      </div>
      <p className="text-sm text-zinc-600 mb-6 max-w-3xl">
        {isMb
          ? "Every lead from links with your tracking code. Firms are shown as Law Firm 1, 2, 3… so you can compare them."
          : "Every landing-page lead, with the media buyer credited, the firm that received it and what it sold for."}
      </p>
      {isMb && <MediaBuyerLinksCard code={code} origin={publicOrigin()} />}
      {isMb && <WeeklyReportToggle />}
      {isMb && <BusyAlertsCard />}
      {["super_admin", "admin"].includes(user?.role) && <WeeklyReportAdminCard />}
      <ResultsFilters f={f} set={set} mediaBuyers={data?.media_buyers || []} showAdmin={showAdmin} />
      <ResultsTotals totals={data?.totals} showPrices={Boolean(data?.show_prices)} active={f.outcome} onPick={(o) => set({ outcome: o })} />
      <ResultsBreakdown data={data} showPrices={Boolean(data?.show_prices)} onFilter={(k, v) => set({ [k]: v })} />
      <BestHours data={data} />
      {data?.truncated && <div className="text-[11px] text-amber-800 mb-2" data-testid="results-truncated">Showing the newest 20,000 leads — narrow the dates to see everything.</div>}
      <ResultsTable data={data} showAdmin={showAdmin} onPage={setPageNum} />
    </div>
  );
}
